import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import {
	GetAgreementDetailsResponseModel,
	GetPaymentCollectionOptionsResponseModel,
	GetPaymentDetailsResponseModel,
	PayNowClient,
	PolicyExceptionReason,
} from '@acorn-portal/shared/src/services/api';
import { IMissingPaymentFormValues } from '@acorn-portal/shared/src/hooks/useMissingPaymentForm';
import axiosRetry from 'axios-retry';

export type IStatus = 'initial' | 'idle' | 'loading' | 'failed';

type IError = {
	status: IStatus;
	policyExceptionReason?: PolicyExceptionReason;
};

// TODO: Sort this out logic is all over the place
interface IPayNowDetails extends GetPaymentDetailsResponseModel {
	directDebitId?: string;
	validUntil?: string;
}

export interface IPayNow {
	details: IPayNowDetails | undefined;
	collectionOptions: GetPaymentCollectionOptionsResponseModel[];
	missingPaymentFormDetails: IMissingPaymentFormValues;
	status: IStatus;
	policyExceptionReason?: PolicyExceptionReason;
}

const initialState: IPayNow = {
	details: undefined,
	collectionOptions: [],
	missingPaymentFormDetails: {
		email: '',
		proceed: false,
		vaultCardOptOut: false,
	},
	status: 'initial',
};

export const getPayNowDetails = createAsyncThunk<GetPaymentDetailsResponseModel, string, { rejectValue: IError }>(
	'payNow/getPayNowDetail',
	async (pr: string, thunkAPI) => {
		const client = new PayNowClient(undefined, API);
		try {
			const result = await client.getPaymentDetails(pr);
			return result;
		} catch (err: any) {
			if (err.status === 409) {
				return thunkAPI.rejectWithValue({
					status: 'failed',
					policyExceptionReason: err.policyExceptionReason,
				});
			}
			return thunkAPI.rejectWithValue({ status: 'failed' });
		}
	},
);

export const getPayNowDetailsAndPaymentOptions = createAsyncThunk<
	{
		payNowDetails: GetAgreementDetailsResponseModel;
		collectionOptions: GetPaymentCollectionOptionsResponseModel[];
	},
	string
>('payNow/getPayNowDetailsAndPaymentOptions', async (token: string, { rejectWithValue }) => {
	try {
		axiosRetry(API, { retries: 3 });
		const client = new PayNowClient(undefined, API);
		const payNowDetails = await client.getAgreementDetailsByToken(token);

		if (!payNowDetails?.directDebitId) return { payNowDetails, collectionOptions: [] };

		const collectionOptions = await client.getPaymentCollectionOptions({
			defaultedDirectDebitId: payNowDetails?.directDebitId,
		});

		return { payNowDetails, collectionOptions };
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const payNowSlice = createSlice({
	name: 'payNow',
	initialState,
	reducers: {
		clearPayNowDetails: () => ({
			...initialState,
		}),

		updatePayNowDetails: (state, action: PayloadAction<Partial<IPayNow>>) => ({
			...state,
			...action.payload,
		}),
	},
	extraReducers: (builder) => {
		builder.addCase(getPayNowDetails.pending, (state) => {
			state.status = 'loading';
		});
		builder.addCase(getPayNowDetails.fulfilled, (state, action) => {
			state.details = {
				...state.details,
				...action.payload,
			};
			state.status = 'idle';
		});
		builder.addCase(getPayNowDetails.rejected, (state, action) => {
			state.status = 'failed';
			state.policyExceptionReason = action.payload?.policyExceptionReason;
		});

		builder.addCase(getPayNowDetailsAndPaymentOptions.pending, (state) => {
			state.status = 'loading';
		});
		builder.addCase(getPayNowDetailsAndPaymentOptions.fulfilled, (state, action) => {
			const { payNowDetails, collectionOptions } = action.payload;
			state.details = {
				...state.details,
				...payNowDetails,
				policyHolderFirstName: '',
				policyHolderLastName: '',
				policyHolderCompanyName: '',
				policyReference: '',
				email: '',
				customerId: '',
				totalBalanceOutstanding: 0,
				unaccountedForBalance: 0,
				amountDueOnNextDirectDebit: 0,
				agreementId: payNowDetails.agreementId ?? '',
				splitPaymentPlans: payNowDetails.splitPaymentPlans ?? [],
			};
			state.collectionOptions = collectionOptions;
			state.status = 'idle';
		});
		builder.addCase(getPayNowDetailsAndPaymentOptions.rejected, (state) => {
			state.status = 'failed';
		});
	},
});

export const { updatePayNowDetails, clearPayNowDetails } = payNowSlice.actions;
export default payNowSlice.reducer;
